import type { RefObject } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { getWindowSafe } from '@change-corgi/core/window';

type StickyStyles = {
	position: 'sticky';
	top: string;
};

const MARGIN_THRESHOLD = 60;

/**
 * This powers the sticky signature block on large breakpoints. The signature block is only made sticky if it fits
 * within the screen. It also accounts for lazy-loaded content inside the signature block, which can change its height.
 * Even after all content is loaded, the block's height may still change. This is why a ResizeObserver is used.
 */
export const useStickyBehavior = (
	signatureBlockRef: RefObject<HTMLDivElement | null>,
): Readonly<StickyStyles> | EmptyObject => {
	const [canBeSticky, setCanBeSticky] = useState(false);

	const windowObject = getWindowSafe();

	const isResizeObserverSupported = useMemo(
		() => typeof windowObject !== 'undefined' && 'ResizeObserver' in windowObject,
		[windowObject],
	);

	useEffect(() => {
		if (
			!isResizeObserverSupported ||
			!signatureBlockRef.current ||
			!windowObject // windowObject should be defined at this point, adding it only to prevent type issues
		) {
			return undefined;
		}
		setCanBeSticky(signatureBlockRef.current.offsetHeight + MARGIN_THRESHOLD < windowObject.innerHeight);

		const observer = new windowObject.ResizeObserver(([entry]: ResizeObserverEntry[]) => {
			setCanBeSticky(entry.contentRect.height + MARGIN_THRESHOLD < windowObject.innerHeight);
		});

		observer.observe(signatureBlockRef.current);

		return () => {
			observer.disconnect();
		};
	}, [signatureBlockRef, isResizeObserverSupported, windowObject]);

	return canBeSticky ? { position: 'sticky', top: '1rem' } : {};
};
